import { Button } from '@bambi/component-library/src/components/Button/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { SearchTypeahead } from '@bambi/component-library/src/components/SearchTypeahead/SearchTypeahead';
import type {
  ColumnDef,
  PaginationState,
  Row,
  Updater,
} from '@tanstack/react-table';
import { createColumnHelper, functionalUpdate } from '@tanstack/react-table';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { InvoicedTripsValue } from '../constants';
import { tripFinderModalColumns } from '../management/TripManagementDataGrid/columns';
import { TripManagementFilters } from '../management/TripManagementFilters';
import {
  clearTripFinderSelectedTrips,
  setTripFinderSelectedTrip,
  setTripSearchTerm,
} from '../trip.slice';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { DataGrid } from 'src/common/DataGrid';
import type { InvoiceTripFinderRead } from 'src/common/external/bambi-api/bambiApi';
import {
  useInvoicesTripFinderListQuery,
  useLazyInvoicesTripFinderListQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { DEFAULT_PAGINATION_PAGE_SIZE } from 'src/common/external/bambi-api/constants';
import type { TripsListApiArg } from 'src/common/external/bambi-api/tripsApi';
import type { ModalProps } from 'src/common/primitives/Modal';
import { Modal } from 'src/common/primitives/Modal';
import { show } from 'src/common/primitives/Toast/toast.slice';

const columnHelper = createColumnHelper<InvoiceTripFinderRead>();

function RowSelectionCell({ row }: { row: Row<InvoiceTripFinderRead> }) {
  const dispatch = useAppDispatch();
  return row.getCanSelect() ? (
    <input
      data-testid="select-trip-checkbox"
      type="checkbox"
      checked={row.getIsSelected()}
      onChange={(e) => {
        const trip = row.original;
        dispatch(
          setTripFinderSelectedTrip({
            tripId: trip.id,
            trip: trip,
          })
        );
      }}
    />
  ) : null;
}
const modalColumns: ColumnDef<InvoiceTripFinderRead, unknown>[] = [
  columnHelper.display({
    id: 'row-selection',
    header: '',
    cell: ({ row }) => <RowSelectionCell row={row} />,
  }),
  ...tripFinderModalColumns,
];

export interface TripFinderModalProps {
  open: ModalProps['open'];
  setOpen: ModalProps['setOpen'];
  onConfirm: (selectedTrips: InvoiceTripFinderRead[]) => void;
}

export function TripFinderModal({
  open,
  setOpen,
  onConfirm,
}: TripFinderModalProps) {
  const filterPrice = useAppSelector((state) => state.trip.filterPrice);
  const filterVehicles = useAppSelector((state) => state.trip.filterVehicles);
  const selectedCreatedAtDateRange = useAppSelector(
    (state) => state.trip.selectedCreatedAtDateRange
  );
  const selectedDateRange = useAppSelector(
    (state) => state.trip.selectedDateRange
  );
  const selectedPayers = useAppSelector((state) => state.trip.selectedPayers);
  const selectedStatuses = useAppSelector(
    (state) => state.trip.selectedStatuses
  );
  const selectedTags = useAppSelector((state) => state.trip.selectedTags);
  const tripSearchTerm = useAppSelector((state) => state.trip.tripSearchTerm);
  const selectedTrips = useAppSelector(
    (state) => state.trip.tripFinder.selectedTrips
  );

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGINATION_PAGE_SIZE,
  });

  const ordering = useSearchParams()[0].get(
    'ordering'
  ) as TripsListApiArg['ordering'];

  const filterInvoicedTrips = useAppSelector(
    (state) => state.trip.filterInvoicedTrips
  );

  const hasInvoiceValue = (() => {
    switch (filterInvoicedTrips) {
      case InvoicedTripsValue.ALL_TRIPS:
        return undefined;
      case InvoicedTripsValue.INVOICED_TRIPS:
        return true;
      case InvoicedTripsValue.NOT_INVOICED_TRIPS:
        return false;
    }
  })();

  const hasUnpaidInvoiceValue =
    filterInvoicedTrips === InvoicedTripsValue.UNPAID_INVOICE_TRIPS
      ? true
      : undefined;

  const {
    data: trips,
    isLoading,
    isFetching,
  } = useInvoicesTripFinderListQuery(
    {
      page: pageIndex + 1,
      search: tripSearchTerm,
      rangeStart: selectedDateRange?.from || '',
      rangeEnd: selectedDateRange?.to || '',
      createdAtRangeStart: selectedCreatedAtDateRange?.from || '',
      createdAtRangeEnd: selectedCreatedAtDateRange?.to || '',
      payers: selectedPayers.join(',') || '',
      status: selectedStatuses.join(',') || '',
      tags: selectedTags.join(',') || '',
      hasInvoice: hasInvoiceValue,
      hasUnpaidInvoice: hasUnpaidInvoiceValue,
      vehicles: filterVehicles.join(',') || '',
      ordering,
      priceMin: filterPrice?.min ?? undefined,
      priceMax: filterPrice?.max ?? undefined,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const [fetchTrips] = useLazyInvoicesTripFinderListQuery();
  const [addingAllTrips, setAddingAllTrips] = useState(false);

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const handlePaginationChange = (
    paginationUpdater: Updater<PaginationState>
  ) => {
    setPagination((old) => functionalUpdate(paginationUpdater, old));
  };

  const dispatch = useAppDispatch();

  const handleSearch = (term: string) => {
    dispatch(setTripSearchTerm(term));
  };

  const debounceSearch = debounce(handleSearch, 300);

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: DEFAULT_PAGINATION_PAGE_SIZE });
  }, [
    tripSearchTerm,
    selectedDateRange,
    selectedPayers,
    selectedTags,
    open,
    ordering,
    filterPrice,
  ]);

  const selectedTripsList = useMemo(() => {
    return Object.values(selectedTrips).filter(
      (trip): trip is InvoiceTripFinderRead => trip !== undefined
    );
  }, [selectedTrips]);

  const handleAddSelected = useCallback(() => {
    onConfirm(selectedTripsList);
  }, [onConfirm, selectedTripsList]);

  const handleAddAll = useCallback(async () => {
    setAddingAllTrips(true);

    const { results } = await fetchTrips({
      search: tripSearchTerm,
      rangeStart: selectedDateRange?.from || '',
      rangeEnd: selectedDateRange?.to || '',
      payers: selectedPayers.join(',') || '',
      status: selectedStatuses.join(',') || '',
      tags: selectedTags.join(',') || '',
      hasInvoice: false,
      vehicles: filterVehicles.join(',') || '',
      ordering,
      priceMin: filterPrice?.min ?? undefined,
      priceMax: filterPrice?.max ?? undefined,
      // Adjust pageSize if we need to get more than 1000 trips
      pageSize: 1000,
    }).unwrap();

    // Ensure we don't have any trips that do have an invoice
    const filteredTrips = results.filter((trip) => !trip.invoice_id);

    // Shouldn't happen because the button is disabled when no trips are available
    if (!filteredTrips.length) {
      setAddingAllTrips(false);
      dispatch(
        show({
          type: 'info',
          title: 'No trips available',
          description: 'No trips available in list, please reduce your filters',
        })
      );
      return;
    }

    setAddingAllTrips(false);
    onConfirm(filteredTrips);
  }, [
    dispatch,
    fetchTrips,
    filterVehicles,
    onConfirm,
    ordering,
    selectedDateRange?.from,
    selectedDateRange?.to,
    selectedPayers,
    selectedStatuses,
    selectedTags,
    tripSearchTerm,
    filterPrice,
  ]);

  // Reset search and selected trips when modal opens/closes
  useEffect(() => {
    dispatch(setTripSearchTerm(''));
    dispatch(clearTripFinderSelectedTrips());
  }, [open, dispatch]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      contentClassnames="w-full max-w-[75vw]"
      title="Trip Finder"
    >
      <div data-testid="trip-finder-modal">
        <div className="mb-2 flex flex-row items-center justify-between">
          <TripManagementFilters />
          <div className="pt-4">
            <SearchTypeahead
              onChange={debounceSearch}
              placeholder="Search trips"
              dataTestId="trip-finder-search"
            />
          </div>
        </div>
        {isLoading || isFetching ? (
          <div className="my-72 flex justify-center">
            <LoadingIndicator />
          </div>
        ) : (
          <DataGrid
            tableId="trip-finder"
            containerClassName="max-h-[50vh]"
            columns={modalColumns as ColumnDef<InvoiceTripFinderRead, any>[]}
            data={trips?.results || []}
            pagination={pagination}
            totalCount={trips?.count || 0}
            handlePaginationChange={handlePaginationChange}
            rowSelection={trips?.results.reduce((acc, trip, index) => {
              if (!selectedTrips[trip.id]) {
                return acc;
              }
              return {
                ...acc,
                [index]: true,
              };
            }, {})}
            emptyState={<div>No trips found</div>}
            enableRowSelection={(row) => row.original.invoice_numbers === null}
          />
        )}
        <div className="mt-4 flex flex-row justify-between">
          <Button dataTestId="cancelBtn" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <div className="flex flex-row gap-2">
            <Button
              variant="primary-outline"
              disabled={trips?.count === 0}
              onClick={handleAddAll}
              loading={addingAllTrips}
              dataTestId="addAllTrips"
            >
              Add all Invoiceable Trips
            </Button>
            <Button
              type="submit"
              variant="primary"
              onClick={handleAddSelected}
              disabled={selectedTripsList.length === 0}
              dataTestId="confirmBtn"
            >
              Add{' '}
              {selectedTripsList.length ? `(${selectedTripsList.length})` : ''}{' '}
              Trips
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
